import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";

const SCRIPT_ID = "recaptcha-enterprise-script";
const WIDGET_ID_ATTR = "data-recaptcha-widget-id";

/**
 * reCAPTCHA Enterprise - Checkbox (desafío "No soy un robot").
 * Carga enterprise.js y renderiza el widget; expone getValue() y reset() vía ref.
 * @see https://docs.cloud.google.com/recaptcha/docs/instrument-web-pages-with-checkbox
 */
const RecaptchaEnterprise = forwardRef(function RecaptchaEnterprise(
    { siteKey, action = "submit", onChange, onExpired, className = "" },
    ref
) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);

    useImperativeHandle(ref, () => ({
        getValue() {
            if (typeof window === "undefined" || !window.grecaptcha?.enterprise) return "";
            const wid = containerRef.current?.getAttribute(WIDGET_ID_ATTR);
            if (wid == null) return "";
            try {
                return window.grecaptcha.enterprise.getResponse(parseInt(wid, 10)) || "";
            } catch (e) {
                return "";
            }
        },
        reset() {
            if (typeof window === "undefined" || !window.grecaptcha?.enterprise) return;
            const wid = containerRef.current?.getAttribute(WIDGET_ID_ATTR);
            if (wid != null) {
                try {
                    window.grecaptcha.enterprise.reset(parseInt(wid, 10));
                } catch (e) {
                    // ignore
                }
            }
        },
    }));

    useEffect(() => {
        if (!siteKey || !containerRef.current) return;

        const renderWidget = () => {
            if (!containerRef.current) return;
            const container = containerRef.current;
            if (container.getAttribute(WIDGET_ID_ATTR)) return;
            const grecaptchaEnterprise = window.grecaptcha?.enterprise;
            if (!grecaptchaEnterprise || typeof grecaptchaEnterprise.render !== "function") {
                return;
            }
            const widgetId = grecaptchaEnterprise.render(container, {
                sitekey: siteKey,
                action: action,
                callback: (token) => {
                    if (onChange) onChange(token);
                },
                "expired-callback": () => {
                    if (onChange) onChange("");
                    if (onExpired) onExpired();
                },
            });
            container.setAttribute(WIDGET_ID_ATTR, String(widgetId));
            widgetIdRef.current = widgetId;
        };

        const tryRender = () => {
            const gr = window.grecaptcha?.enterprise;
            if (!gr) return;
            if (typeof gr.ready === "function") {
                gr.ready(() => {
                    if (typeof window.grecaptcha?.enterprise?.render === "function") {
                        renderWidget();
                    }
                });
                return;
            }
            if (typeof gr.render === "function") {
                renderWidget();
            }
        };

        if (document.getElementById(SCRIPT_ID)) {
            const check = setInterval(() => {
                if (window.grecaptcha?.enterprise) {
                    clearInterval(check);
                    tryRender();
                }
            }, 150);
            return () => clearInterval(check);
        }

        const script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.src = "https://www.google.com/recaptcha/enterprise.js?render=explicit&onload=onRecaptchaEnterpriseLoad";
        script.async = true;
        script.defer = true;
        window.onRecaptchaEnterpriseLoad = () => {
            window.onRecaptchaEnterpriseLoad = null;
            tryRender();
        };
        document.head.appendChild(script);
        return () => {
            if (window.grecaptcha?.enterprise && widgetIdRef.current != null) {
                try {
                    window.grecaptcha.enterprise.reset(widgetIdRef.current);
                } catch (e) {}
            }
        };
    }, [siteKey, action, onChange, onExpired]);

    return <div ref={containerRef} className={className} />;
});

export default RecaptchaEnterprise;
